/**
 * Navegación inferior de 5 pestañas. "Pelear", "Mejoras" y "Equipo" están
 * activas; las últimas 2 muestran candado y disparan `onLocked` en vez de
 * cambiar de pestaña (roadmap: misiones, liga).
 */

import Phaser from 'phaser';
import { PALETTE } from './palette';

export interface NavTabDef {
  id: string;
  label: string;
  locked?: boolean;
}

export const NAV_HEIGHT = 76;

interface TabHandle {
  def: NavTabDef;
  bg: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  icon: Phaser.GameObjects.Graphics;
  zone: Phaser.GameObjects.Zone;
}

/** Dibuja un glifo simple por pestaña (evita depender de más texturas horneadas). */
function drawTabIcon(g: Phaser.GameObjects.Graphics, id: string, active: boolean, locked: boolean): void {
  const color = locked ? PALETTE.textDim : active ? PALETTE.accent : PALETTE.textMuted;
  g.clear();
  g.fillStyle(color, 1);
  g.lineStyle(2, color, 1);
  switch (id) {
    case 'pelear':
      g.fillTriangle(-6, -7, -6, 7, 8, 0);
      break;
    case 'mejoras':
      g.fillRect(-7, 2, 4, 6);
      g.fillRect(-1, -3, 4, 11);
      g.fillRect(5, -7, 4, 15);
      break;
    case 'equipo':
      g.strokeRoundedRect(-8, -7, 16, 14, 3);
      g.fillRect(-3, -10, 6, 5);
      break;
    default:
      g.fillCircle(0, -2, 7);
      g.fillRoundedRect(-2, 3, 4, 6, 1);
      break;
  }
  if (locked) {
    g.fillStyle(PALETTE.bg, 1);
    g.fillCircle(9, 9, 7);
    g.lineStyle(2, PALETTE.textDim, 1);
    g.strokeCircle(9, 9, 7);
    g.fillStyle(PALETTE.textDim, 1);
    g.fillRect(7, 7, 4, 4);
  }
}

export class BottomNav extends Phaser.GameObjects.Container {
  private readonly tabs: TabHandle[] = [];
  private activeId: string;

  constructor(
    scene: Phaser.Scene,
    width: number,
    y: number,
    tabDefs: NavTabDef[],
    initialActive: string,
    private readonly onSelect: (id: string) => void,
    private readonly onLocked: (def: NavTabDef) => void
  ) {
    super(scene, 0, y);
    this.activeId = initialActive;

    const bar = scene.add.graphics();
    bar.fillStyle(PALETTE.panel, 1);
    bar.fillRect(0, 0, width, NAV_HEIGHT);
    bar.lineStyle(2, PALETTE.border, 1);
    bar.lineBetween(0, 0, width, 0);
    this.add(bar);

    const tabWidth = width / tabDefs.length;
    tabDefs.forEach((def, i) => {
      const cx = tabWidth * i + tabWidth / 2;
      const cy = NAV_HEIGHT / 2;

      const bg = scene.add.graphics();
      const icon = scene.add.graphics({ x: cx, y: cy - 10 });
      const label = scene.add
        .text(cx, cy + 16, def.label, {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#a99fd1',
          align: 'center',
        })
        .setOrigin(0.5);

      const zone = scene.add.zone(cx, cy, tabWidth, NAV_HEIGHT).setInteractive({ useHandCursor: true });
      zone.on('pointerdown', () => this.handleTap(def));

      this.add([bg, icon, label, zone]);
      this.tabs.push({ def, bg, label, icon, zone });
    });

    this.setSize(width, NAV_HEIGHT);
    scene.add.existing(this);
    this.redraw();
  }

  private handleTap(def: NavTabDef): void {
    if (def.locked) {
      this.onLocked(def);
      return;
    }
    if (def.id === this.activeId) return;
    this.activeId = def.id;
    this.redraw();
    this.onSelect(def.id);
  }

  private redraw(): void {
    const tabWidth = (this.tabs[0]?.zone.width ?? 0) || 0;
    for (const tab of this.tabs) {
      const active = tab.def.id === this.activeId;
      tab.bg.clear();
      if (active) {
        const cx = tab.zone.x;
        tab.bg.fillStyle(PALETTE.panel2, 1);
        tab.bg.fillRoundedRect(cx - tabWidth / 2 + 6, 6, tabWidth - 12, NAV_HEIGHT - 12, 10);
      }
      drawTabIcon(tab.icon, tab.def.id, active, tab.def.locked === true);
      tab.label.setColor(tab.def.locked ? '#6f6394' : active ? '#ffb703' : '#a99fd1');
    }
  }
}
